/**
 * SupabaseDocStore — the BROWSER {@link DocStorePort} adapter over PostgREST
 * (supabase-js), completing the portal's data cutover (plan 002, Phase 8):
 * the portal's typed repos (resolveSlug / get / settings reads + the
 * sign-in profile stub) run UNCHANGED over Supabase, RLS-gated by the user's
 * Firebase JWT exactly as Firestore rules gated them.
 *
 * Same registry as the Node adapter — one mapping, three transports.
 *
 * SCOPE: the port surface the BROWSER actually uses — reads, plain set/merge/
 * update/delete (RLS-permitted ones only, e.g. the creator-profile stub).
 * Atomic field ops (increment/arrayUnion), guardedUpdate CAS, and multi-doc
 * batch are SERVER concerns (every counter/roster mutation goes through the
 * trusted callables — Firestore rules denied them client-side too) and throw
 * a clear PlatformError if ever reached.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  PlatformError,
  isFieldSentinel as isSentinel,
  isServerTimestampSentinel,
  type BatchOp,
  type DocStorePort,
  type FieldArrayUnion,
  type FieldIncrement,
  type QuerySpec,
  type ServerTimestampSentinel,
} from '@djgg/core';
import {
  TABLES,
  VIRTUAL,
  camelToSnake,
  collectionKeyValues,
  fieldToColumn,
  resolvePath,
  type TableDef,
} from './registry.js';
import {
  CREATIONS_CLIENT_SELECT,
  CREATIONS_WITH_MEMBERS_SELECT,
  rowToDoc,
  stitchCreationMembers,
  type MemberRow,
  type Row,
} from './rows.js';
import { resolveJsonSentinels } from './nestedSentinels.js';

const TS = { __djggServerTimestamp: true } as const;

export class SupabaseDocStore implements DocStorePort {
  constructor(private readonly client: SupabaseClient) {}

  serverTimestamp(): ServerTimestampSentinel {
    return TS;
  }
  increment(by: number): FieldIncrement {
    return { __djggIncrement: by } as FieldIncrement;
  }
  arrayUnion(...values: unknown[]): FieldArrayUnion {
    return { __djggArrayUnion: values } as FieldArrayUnion;
  }

  private def(key: string, path: string): TableDef {
    const def = TABLES[key];
    if (!def) throw new PlatformError('validation', `no table mapping for path ${path}`);
    return def;
  }

  async getDoc<T>(path: string): Promise<T | null> {
    const { key, ids } = resolvePath(path);
    if (key === VIRTUAL.slugs) {
      const { data, error } = await this.client
        .from('creations')
        .select('id, creator_uid, created_at')
        .eq('slug', ids[0])
        .maybeSingle();
      if (error) throw toPlatformError(error);
      if (!data) return null;
      return {
        creationId: data.id,
        ownerUid: data.creator_uid,
        claimedAt: data.created_at,
      } as T;
    }
    if (key.endsWith(`/${VIRTUAL.viewShards}`)) {
      const { data, error } = await this.client
        .from('creations')
        .select('views_count')
        .eq('id', ids[0])
        .maybeSingle();
      if (error) throw toPlatformError(error);
      return data ? ({ count: Number(ids[1]) === 0 ? data.views_count : 0 } as T) : null;
    }
    const def = this.def(key, path);
    const kv = def.keyOf(ids);
    const sel: string = def.table === 'creations' ? CREATIONS_WITH_MEMBERS_SELECT : '*';
    let q = this.client.from(def.table).select(sel);
    for (const [col, v] of Object.entries(kv)) q = q.eq(col, v);
    const { data, error } = await q.maybeSingle();
    if (error) throw toPlatformError(error);
    if (!data) return null;
    return this.toDoc(def, data as unknown as Row) as T;
  }

  async query<T>(collectionPath: string, spec?: QuerySpec): Promise<Array<T & { id: string }>> {
    const { key, ids } = resolvePath(collectionPath + '/x');
    const def = this.def(key, collectionPath);
    // membership filter (the PostgREST inner-join form of array-contains) must
    // shape the SELECT itself — decide it BEFORE building the chain, so every
    // other where clause composes on top (rebuilding mid-loop dropped them:
    // review finding).
    const memberUid = (spec?.where ?? []).find(
      ([f, op]) => op === 'array-contains' && f === 'memberUids',
    )?.[2] as string | undefined;
    const baseSel = def.table === 'creations' ? CREATIONS_WITH_MEMBERS_SELECT : '*';
    const sel: string = memberUid === undefined ? baseSel : `${baseSel}, member_filter:creation_members!inner(uid)`;
    let q = this.client.from(def.table).select(sel);
    if (memberUid !== undefined) q = q.eq('member_filter.uid', memberUid);
    // parent scope for nested collections
    if (key.includes('/')) {
      const kv = collectionKeyValues(def, ids);
      for (const [col, v] of Object.entries(kv)) {
        q = q.eq(col, v);
      }
    }
    for (const [field, op, value] of spec?.where ?? []) {
      if (op === 'array-contains' && field === 'memberUids') continue; // applied above
      const col = fieldToColumn(def, field, 'postgrest');
      if (op === '==') q = q.eq(col, value);
      else if (op === '!=') q = q.neq(col, value);
      else if (op === '<') q = q.lt(col, value);
      else if (op === '<=') q = q.lte(col, value);
      else if (op === '>') q = q.gt(col, value);
      else if (op === '>=') q = q.gte(col, value);
      else if (op === 'in') q = q.in(col, value as unknown[]);
      else throw new PlatformError('validation', `unsupported query op ${op} on ${col}`);
    }
    for (const [field, dir] of spec?.orderBy ?? []) {
      q = q.order(fieldToColumn(def, field, 'postgrest'), { ascending: dir !== 'desc' });
    }
    if (spec?.limit) q = q.limit(spec.limit);
    const { data, error } = await q;
    if (error) throw toPlatformError(error);
    return ((data ?? []) as unknown as Row[]).map((row) => ({
      ...(this.toDoc(def, row) as T),
      id: String(row[def.docIdColumn]),
    }));
  }

  /** Cross-parent count via a PostgREST head request. NOTE: the count is
   *  RLS-FILTERED to what the caller may read — fine for the public per-user
   *  counts the portal may surface; badge EVALUATION always runs server-side
   *  over the owner-connected Postgres adapter. */
  async countGroup(collectionGroup: string, where: QuerySpec['where'] = []): Promise<number> {
    const def = this.def(collectionGroup, collectionGroup);
    const select = def.table === 'creations' ? CREATIONS_CLIENT_SELECT : '*';
    let q = this.client.from(def.table).select(select, { count: 'exact', head: true });
    for (const [field, op, value] of where) {
      const col = fieldToColumn(def, field, 'postgrest');
      if (value === null && (op === '==' || op === '!=')) {
        q = op === '==' ? q.is(col, null) : q.not(col, 'is', null);
      } else if (op === '==') q = q.eq(col, value);
      else if (op === '!=') q = q.neq(col, value);
      else if (op === '<') q = q.lt(col, value);
      else if (op === '<=') q = q.lte(col, value);
      else if (op === '>') q = q.gt(col, value);
      else if (op === '>=') q = q.gte(col, value);
      else if (op === 'in') q = q.in(col, value as unknown[]);
      else throw new PlatformError('validation', `countGroup: unsupported op ${op} on ${col}`);
    }
    const { count, error } = await q;
    if (error) throw toPlatformError(error);
    return count ?? 0;
  }

  async setDoc<T extends Record<string, unknown>>(path: string, data: T): Promise<void> {
    // Plain INSERT, not upsert: every browser setDoc creates a NEW doc (events,
    // nickname claims, exists-checked profile stubs), and a PostgREST upsert
    // would demand UPDATE grants — incl. the conflict key — for create-only
    // writes (live 42501 finding). A create race surfaces as a unique-violation
    // error, which is ALSO the Firestore-era create-only semantic the review
    // flagged as lost.
    const { key, ids } = resolvePath(path);
    if (key === VIRTUAL.slugs || key.endsWith(`/${VIRTUAL.viewShards}`)) {
      throw new PlatformError('validation', `client writes to virtual path ${path} are server-only`);
    }
    const def = this.def(key, path);
    const kv = def.keyOf(ids);
    const row = { ...this.toRow(def, data), ...kv };
    const { error } = await this.client.from(def.table).insert(row);
    if (error) throw toPlatformError(error);
  }

  async mergeDoc<T extends Record<string, unknown>>(path: string, data: Partial<T>): Promise<void> {
    await this.upsert(path, data as Record<string, unknown>);
  }

  async update(path: string, data: Record<string, unknown>): Promise<void> {
    const { key, ids } = resolvePath(path);
    const def = this.def(key, path);
    const kv = def.keyOf(ids);
    let q = this.client.from(def.table).update(this.toRow(def, data));
    for (const [col, v] of Object.entries(kv)) q = q.eq(col, v);
    // .select() surfaces the affected rows — 0 rows (missing doc OR an
    // RLS-filtered one) must throw like the other two adapters (port contract).
    const { data: rows, error } = await q.select(def.docIdColumn);
    if (error) throw toPlatformError(error);
    if (!rows || rows.length === 0) {
      throw new PlatformError('not-found', `update: no doc at ${path}`);
    }
  }

  async deleteDoc(path: string): Promise<void> {
    const { key, ids } = resolvePath(path);
    const def = this.def(key, path);
    const kv = def.keyOf(ids);
    let q = this.client.from(def.table).delete();
    for (const [col, v] of Object.entries(kv)) q = q.eq(col, v);
    const { error } = await q;
    if (error) throw toPlatformError(error);
  }

  async batch(_ops: BatchOp[]): Promise<void> {
    throw new PlatformError('validation', 'batch is server-only (use the callables)');
  }

  async guardedUpdate(): Promise<boolean> {
    throw new PlatformError('validation', 'guardedUpdate is server-only (use the callables)');
  }

  // ---- helpers -------------------------------------------------------------
  private async upsert(path: string, data: Record<string, unknown>): Promise<void> {
    const { key, ids } = resolvePath(path);
    if (key === VIRTUAL.slugs || key.endsWith(`/${VIRTUAL.viewShards}`)) {
      throw new PlatformError('validation', `client writes to virtual path ${path} are server-only`);
    }
    const def = this.def(key, path);
    const kv = def.keyOf(ids);
    const row = { ...this.toRow(def, data), ...kv };
    const { error } = await this.client
      .from(def.table)
      .upsert(row, { onConflict: Object.keys(kv).join(',') });
    if (error) throw toPlatformError(error);
  }

  /** doc fields → row (browser subset: plain values + serverTimestamp→now). */
  private toRow(def: TableDef, data: Record<string, unknown>): Row {
    const row: Row = {};
    const extras: Row = {};
    const nestedTimestampMs = Date.now();
    for (const [field, value] of Object.entries(data)) {
      if (value === undefined) continue;
      if (def.derived?.has(field)) continue;
      const resolved = isServerTimestampSentinel(value) ? Date.now() : value;
      if (isSentinel(resolved)) {
        throw new PlatformError(
          'validation',
          `atomic field op on '${field}' is server-only (use the callables)`,
        );
      }
      const flat = def.flatten?.[field];
      if (flat && resolved && typeof resolved === 'object') {
        for (const [k, col] of Object.entries(flat)) {
          const v = (resolved as Row)[k];
          if (v !== undefined) row[col] = v;
        }
        continue;
      }
      const col = camelToSnake(field);
      if (def.jsonb?.has(field)) row[col] = resolveJsonSentinels(resolved, nestedTimestampMs);
      else if (def.extrasColumn && def.columns && !def.columns.has(col)) {
        extras[field] = resolveJsonSentinels(resolved, nestedTimestampMs);
      }
      else row[col] = resolved;
    }
    if (def.extrasColumn && Object.keys(extras).length > 0) row[def.extrasColumn] = extras;
    return row;
  }

  private toDoc(def: TableDef, row: Row): Row {
    const { creation_members: members, member_filter: _mf, ...rest } = row as Row & {
      creation_members?: MemberRow[];
      member_filter?: unknown;
    };
    const doc = rowToDoc(def, rest);
    if (def.table === 'creations') return stitchCreationMembers(doc, members ?? []);
    return doc;
  }
}

function toPlatformError(e: { message?: string; code?: string }): PlatformError {
  return new PlatformError('unavailable', `postgrest: ${e.code ?? ''} ${e.message ?? 'error'}`);
}
